import React from "react";
import {
  Shield,
  Award,
  Microscope,
  Leaf,
  CheckCircle2,
  ClipboardCheck,
  Factory,
  HeartPulse,
  Sparkles,
  Check,
  ArrowLeft,
  ArrowRight,
  FileText,
  Users,
  MessageSquare,
  Target,
  Cog,
  Building2,
} from "lucide-react";
import "./QualityPolicy.css";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const HeroSection = () => {
  const { t } = useTranslation();

  return (
    <section className="qp-hero">
      <div className="qp-shape qp-shape-1"></div>
      <div className="qp-shape qp-shape-2"></div>
      <div className="qp-hero-container">
        <div className="Header_wrapper">
          <span className="Header_subtitle">{t("qp_hero_badge")}</span>
          <h1 className="Header_title">
            {t("qp_hero_title")} {t("qp_hero_highlight")}
          </h1>
        </div>
        <p className="qp-hero-desc">{t("qp_hero_desc")}</p>
        <div className="qp-doc-info">
          <div className="qp-doc-tag">
            <FileText size={14} />
            <span>{t("qp_doc_code")}: PO-001</span>
          </div>
          <div className="qp-doc-tag">
            <span>{t("qp_doc_version")}: 1</span>
          </div>
          <div className="qp-doc-tag">
            <span>{t("qp_doc_date")}: 23/9/2026</span>
          </div>
          <div className="qp-doc-tag">
            <span>{t("qp_doc_page")}</span>
          </div>
        </div>
      </div>
    </section>
  );
};

const IntroSection = () => {
  const { t } = useTranslation();

  return (
    <section className="qp-intro">
      <div className="qp-container">
        <div className="qp-intro-card">
          <div className="qp-intro-icon">
            <Building2 size={32} />
          </div>
          <h2 className="qp-intro-title">
            {t("qp_company_name")}
          </h2>
          <p className="qp-intro-text">{t("qp_intro_text")}</p>
          <div className="qp-intro-divider"></div>
          <p className="qp-intro-motivation">{t("qp_intro_motivation")}</p>
        </div>
      </div>
    </section>
  );
};

const PoliciesSection = () => {
  const { t } = useTranslation();

  const policies = [
    { icon: <CheckCircle2 size={20} />, titleKey: "qp_p1_title", descKey: "qp_p1_desc", num: "01" },
    { icon: <Cog size={20} />, titleKey: "qp_p2_title", descKey: "qp_p2_desc", num: "02" },
    { icon: <Target size={20} />, titleKey: "qp_p3_title", descKey: "qp_p3_desc", num: "03" },
    { icon: <Factory size={20} />, titleKey: "qp_p4_title", descKey: "qp_p4_desc", num: "04" },
    { icon: <Shield size={20} />, titleKey: "qp_p5_title", descKey: "qp_p5_desc", num: "05" },
    { icon: <Users size={20} />, titleKey: "qp_p6_title", descKey: "qp_p6_desc", num: "06" },
    { icon: <Sparkles size={20} />, titleKey: "qp_p7_title", descKey: "qp_p7_desc", num: "07" },
    { icon: <MessageSquare size={20} />, titleKey: "qp_p8_title", descKey: "qp_p8_desc", num: "08" },
    { icon: <ClipboardCheck size={20} />, titleKey: "qp_p9_title", descKey: "qp_p9_desc", num: "09" },
  ];

  return (
    <section className="qp-content">
      <div className="qp-container">
        <div className="qp-section-header">
          <div className="qp-section-tag">
            <ClipboardCheck size={16} />
            <span>{t("qp_section_tag")}</span>
          </div>
          <h2 className="qp-section-title">
            {t("qp_section_title")}{" "}
            <span className="text-red">{t("qp_section_title_highlight")}</span>
          </h2>
          <p className="qp-section-subtitle">{t("qp_section_subtitle")}</p>
        </div>

        <div className="qp-policies-grid">
          {policies.map((policy, index) => (
            <div
              key={index}
              className={`qp-policy-card qp-animate qp-animate-delay-${(index % 6) + 1}`}
            >
              <div className="qp-policy-number">{policy.num}</div>
              <h3 className="qp-policy-card-title">
                {policy.icon}
                {t(policy.titleKey)}
              </h3>
              <p className="qp-policy-card-desc">{t(policy.descKey)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const CertificationsSection = () => {
  const { t } = useTranslation();

  const certs = [
    { icon: <Award size={28} />, titleKey: "qp_cert1_title", subKey: "qp_cert1_sub" },
    { icon: <Shield size={28} />, titleKey: "qp_cert2_title", subKey: "qp_cert2_sub" },
    { icon: <Microscope size={28} />, titleKey: "qp_cert3_title", subKey: "qp_cert3_sub" },
    { icon: <Leaf size={28} />, titleKey: "qp_cert4_title", subKey: "qp_cert4_sub" },
  ];

  return (
    <section className="qp-certifications">
      <div className="qp-container">
        <div className="qp-cert-grid">
          {certs.map((cert, index) => (
            <div
              key={index}
              className={`qp-cert-card qp-animate qp-animate-delay-${index + 1}`}
            >
              <div className="qp-cert-icon">{cert.icon}</div>
              <h3 className="qp-cert-title">{t(cert.titleKey)}</h3>
              <p className="qp-cert-sub">{t(cert.subKey)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const CTASection = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isAr = i18n.language === "ar";

  return (
    <section className="qp-cta">
      <div className="qp-container qp-cta-content">
        <div className="qp-cta-icon">
          <HeartPulse size={32} />
        </div>
        <h2 className="qp-cta-title">{t("qp_cta_title")}</h2>
        <p className="qp-cta-desc">{t("qp_cta_desc")}</p>
        <button className="qp-cta-btn" onClick={() => navigate("/Products")}>
          {t("qp_cta_btn")}
          {isAr ? <ArrowLeft size={18} /> : <ArrowRight size={18} />}
        </button>
      </div>
    </section>
  );
};

export default function QualityPolicy() {
  return (
    <>
      <HeroSection />
      <IntroSection />
      <CertificationsSection />
      <PoliciesSection />
      <CTASection />
    </>
  );
}
